export type PreparedSourcePixels = Readonly<{
  width: number;
  height: number;
  data: Uint8ClampedArray;
  removedPixels: number;
}>;

const WHITE_FLOOR = 238;
const WHITE_CHROMA_LIMIT = 18;

function isBackgroundPixel(data: Uint8ClampedArray, pixel: number): boolean {
  const offset = pixel * 4;
  const alpha = data[offset + 3]!;
  if (alpha === 0) return true;
  const red = data[offset]!;
  const green = data[offset + 1]!;
  const blue = data[offset + 2]!;
  const minimum = Math.min(red, green, blue);
  const maximum = Math.max(red, green, blue);
  return minimum >= WHITE_FLOOR && maximum - minimum <= WHITE_CHROMA_LIMIT;
}

export function removeConnectedWhiteBackground(input: Uint8ClampedArray, width: number, height: number): PreparedSourcePixels {
  if (!Number.isInteger(width) || !Number.isInteger(height) || width < 1 || height < 1 || input.length !== width * height * 4) {
    throw new Error("The decoded source pixels are invalid.");
  }
  const data = new Uint8ClampedArray(input);
  const visited = new Uint8Array(width * height);
  const queue = new Uint32Array(width * height);
  let head = 0;
  let tail = 0;
  let removedPixels = 0;

  const enqueue = (pixel: number) => {
    if (visited[pixel] || !isBackgroundPixel(data, pixel)) return;
    visited[pixel] = 1;
    queue[tail++] = pixel;
  };

  for (let x = 0; x < width; x += 1) {
    enqueue(x);
    if (height > 1) enqueue((height - 1) * width + x);
  }
  for (let y = 1; y < height - 1; y += 1) {
    enqueue(y * width);
    if (width > 1) enqueue(y * width + width - 1);
  }

  while (head < tail) {
    const pixel = queue[head++]!;
    const offset = pixel * 4;
    if (data[offset + 3]! > 0) {
      data[offset + 3] = 0;
      removedPixels += 1;
    }
    const x = pixel % width;
    const y = Math.floor(pixel / width);
    if (x > 0) enqueue(pixel - 1);
    if (x + 1 < width) enqueue(pixel + 1);
    if (y > 0) enqueue(pixel - width);
    if (y + 1 < height) enqueue(pixel + width);
  }

  if (removedPixels === 0) return { width, height, data, removedPixels };

  let left = width;
  let top = height;
  let right = -1;
  let bottom = -1;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (data[(y * width + x) * 4 + 3]! === 0) continue;
      left = Math.min(left, x);
      top = Math.min(top, y);
      right = Math.max(right, x);
      bottom = Math.max(bottom, y);
    }
  }
  if (right < left || bottom < top) return { width, height, data, removedPixels };

  const croppedWidth = right - left + 1;
  const croppedHeight = bottom - top + 1;
  if (croppedWidth === width && croppedHeight === height) return { width, height, data, removedPixels };
  const cropped = new Uint8ClampedArray(croppedWidth * croppedHeight * 4);
  for (let y = 0; y < croppedHeight; y += 1) {
    const sourceStart = ((top + y) * width + left) * 4;
    cropped.set(data.subarray(sourceStart, sourceStart + croppedWidth * 4), y * croppedWidth * 4);
  }
  return { width: croppedWidth, height: croppedHeight, data: cropped, removedPixels };
}
